import { BigNumber, ethers } from "ethers";
import { useWallet } from "hooks";
import { useMultiCallContractInstance } from "hooks/contract/multicall/useMultiCallContract";
import { reduce } from "lodash";
import { useQuery } from "react-query";
import {
  decodeResultMulticallZkSync,
  formatMulticallResults,
} from "utils/contract/decodeResultMulticall";
import {
  getPriceProviderAggregatorZksyncContract,
  getPrimaryIndexToken,
  getUniswapV2Factory,
} from "utils/ethereum/contracts";
import handleGetListTokenProvider from "utils/ethereum/handleGetListTokenProvider";
import { MainNetworkSupported } from "utils/addressUtils";
import { useDecimalToken } from "../useDecimalToken";
import { useGetTokens } from "../useTokenSupported";
import { handleGetDataForBorrowContextZkSync } from "./handleGetDataForBorrowContextZkSync";
import { prepareRequestMulticallZksync } from "./prepareRequestMulticallZksync";

const localChainId = localStorage.getItem("chainId");

const usePrimaryIndexTokenMultiCall = () => {
  const { chainId, account, signer, provider } = useWallet();
  const isMainNet = MainNetworkSupported.includes(+chainId);

  const { availableBorrowTokens, projectTokenList } = useGetTokens();
  const { data: decimalTokens } = useDecimalToken([...availableBorrowTokens, ...projectTokenList]);

  const multicall = useMultiCallContractInstance();

  return useQuery(
    ["borrowed-pit-multicall", account],
    async () => {
      const listToken = [...projectTokenList];

      const PITToken = getPrimaryIndexToken(chainId ?? localChainId);

      const PriceContractInfo = getPriceProviderAggregatorZksyncContract(chainId ?? localChainId);
      const UniswapV2FactoryInfo = getUniswapV2Factory(chainId ?? localChainId);

      const priceProviderList = await handleGetListTokenProvider(
        [...availableBorrowTokens, ...listToken],
        PriceContractInfo,
        provider,
        multicall
      );
      const listPrepareRequestMultical = await prepareRequestMulticallZksync({
        PITToken,
        listToken,
        availableBorrowTokens,
        signerOrProvider: signer,
        account,
        decimalTokens,
        PriceContractInfo,
        chainId,
        UniswapV2FactoryInfo,
      });

      const chunkSize = 200;
      let returnData = [];
      const promises = [];
      for (let i = 0; i < listPrepareRequestMultical.length; i += chunkSize) {
        const listCallData = listPrepareRequestMultical.slice(i, i + chunkSize);
        const valueOfRequest = listCallData.map((r) =>
          "value" in r ? r.value : BigNumber.from(0)
        );
        const totalValue = valueOfRequest.reduce((pre, cur) => pre.add(cur), BigNumber.from(0));
        promises.push(
          multicall.callStatic
            .aggregate3Value(listCallData, {
              value: totalValue,
            })
            .then((resultMulticall) => formatMulticallResults(resultMulticall))
        );
      }

      const allReturnData = await Promise.all(promises);
      allReturnData.forEach((data) => {
        returnData = returnData.concat(data);
      });

      const results = decodeResultMulticallZkSync(listPrepareRequestMultical, returnData);

      const {
        currentLimitLendingToken,
        currentLimitPrjToken,
        pitRemaining,
        totalPITCollateral,
        totalOutstandingAssets,
        accrualAssets,
        healthFactor,
        depositedAssets,
        fTokenAddresses,
        liquidationThresholdList,
        lvrLending,
        evaluations,
        borrwedList,
        pairToken,
      } = handleGetDataForBorrowContextZkSync(results, isMainNet);

      const newTotalOutStanding = reduce(
        totalOutstandingAssets,
        (res, value, key) => {
          const newData = {
            ...value,
            loanBody: ethers.BigNumber.from(value?.value || "0").toString(),
            value: ethers.BigNumber.from(value?.value || "0")
              .add(accrualAssets[key]?.value || 0)
              .toString(),
          };
          return {
            ...res,
            [key]: newData,
          };
        },
        {}
      );

      return {
        availableBorrowTokens,
        currentLimitLendingToken,
        currentLimitPrjToken,
        fTokenAddresses,
        projectTokenList,
        pitCollateralBalance: totalPITCollateral,
        pitRemaining,
        totalOutstandingAssets: newTotalOutStanding,
        accrualAssets,
        healthFactor,
        depositedAssets,
        liquidationThresholdList,
        lvrLending,
        evaluations,
        borrwedList,
        priceProviderList,
        pairToken,
      };
    },
    {
      enabled:
        !!decimalTokens &&
        !!availableBorrowTokens?.length &&
        !!projectTokenList?.length &&
        !!account,
    }
  );
};

export default usePrimaryIndexTokenMultiCall;
